import type { FromBasePlayerInput, FromBaseRow } from './from-base-context';
import { fromBaseRecord, fromBaseUuid } from './from-base-context';
import { readItemField, readItemFieldReference } from './item-field-reader';

export interface EncumbranceApplication { status_id: string; status_item_id: string }

export function readEncumbranceApplications(host: FromBaseRow): EncumbranceApplication[] {
    const computed = readItemFieldReference(host, 'computed');
    const cache = fromBaseRecord(computed) ? readItemFieldReference(computed, '_cache') : null;
    const plans = readItemField({ encumbrance_applications: [fromBaseRecord(cache) ? cache.encumbrance_applications ?? null : null] }, 'encumbrance_applications');
    return plans && typeof plans === 'object' ? Object.values(plans).filter((plan): plan is EncumbranceApplication =>
        fromBaseRecord(plan) && fromBaseUuid(readItemField(plan, 'status_id')) && fromBaseUuid(readItemField(plan, 'status_item_id'))) : [];
}

/** Stable, actor-scoped working identity. The status commit mints the persisted identity. */
export function encumbranceApplication(targetId: string, itemId: string): EncumbranceApplication {
    const target = targetId.replace(/-/g, ''), item = itemId.replace(/-/g, '');
    const hex = [...target].map((digit, index) => (parseInt(digit, 16) ^ parseInt(item[index]!, 16)).toString(16)).join('').toUpperCase();
    return { status_id: `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`, status_item_id: itemId };
}

/** Replay the ordinary status compiler from raw inputs, so every derived cluster sees the new effects exactly once. */
export function projectEncumbranceApplications(base: FromBasePlayerInput, plans: readonly EncumbranceApplication[]): FromBasePlayerInput {
    const targetId = String(readItemField(base.encounter_creatures ?? {}, 'encounter_creature_id'));
    const rows = plans.map(plan => ({ ...plan, status_target_id: targetId, status_caster_id: targetId,
        status_origin_id: plan.status_item_id, status_private: 0, status_stack: 1, status_casting_targets_arr: '',
        status_connected_id: null, status_campaign_id: null,
        ...Object.fromEntries(['origin_point_x', 'origin_point_y', 'origin_point_z', 'distance_point_x', 'distance_point_y',
            'distance_point_z', 'aoe_distance', 'aoe_distance_depth', 'aoe_distance_height',
            ...['starts', 'ends'].flatMap(part => ['year', 'month', 'day', 'hour', 'minute', 'second'].map(unit => `${part}_campaign_${unit}`)),
            'ends_on_target_start_of_turn', 'ends_on_target_end_of_turn', 'ends_on_caster_start_of_next_turn', 'ends_on_caster_end_of_next_turn']
            .map(key => [`status_${key}`, 0])) }));
    const saved = (mode: string): FromBaseRow[] => {
        const value = readItemField({ rows: [base.statuses?.[mode] ?? []] }, 'rows');
        return Array.isArray(value) ? value : [];
    };
    return { ...base, statuses: { ...base.statuses, target: [...saved('target'), ...rows], caster: [...saved('caster'), ...rows] } };
}

/** An automatic ADD decides from saved statuses, excluding only its exact compile-only projection. */
export function withoutEncumbranceApplication(host: FromBaseRow, itemId: string): FromBaseRow {
    const plan = readEncumbranceApplications(host).find(plan => plan.status_item_id === itemId);
    const statuses = readItemField(host, 'statuses');
    if (!plan || !fromBaseRecord(statuses)) return host;
    return { ...host, statuses: Object.fromEntries(Object.entries(statuses).map(([mode, rows]) => [mode,
        rows && typeof rows === 'object' ? Object.values(rows).filter(row => !fromBaseRecord(row)
            || readItemField(row, 'status_id') !== plan.status_id || readItemField(row, 'status_item_id') !== itemId) : rows])) };
}
